import { useState, useCallback } from 'react';
import { SbNotification } from 'uiCore';
import { getJobRequisitionView, getRnCJobRequisitionView, updateJdArchiveStatus } from 'Api/apiService';
import { userHasEntitlement } from 'Utils/parseEntitlements';
import {
  JD_ARCHIVE_STATUS,
  JD_ARCHIVE_ERROR_CODES as ERR,
  JD_ARCHIVE_MESSAGES as MSG,
} from 'constants/jdArchive.constants';

const handleJdArchiveError = (error, fallbackTitle, fallbackDesc) => {
  const errCode = error?.response?.data?.error_code;
  const backendMsg = error?.response?.data?.message || '';

  if (errCode === ERR.EMPLOYEE_ASSIGNED) {
    SbNotification.error(MSG.ERR_EMPLOYEE_ASSIGNED, backendMsg || MSG.ERR_EMPLOYEE_ASSIGNED_DESC);
    return;
  }
  if (errCode === ERR.NOT_FOUND) {
    SbNotification.error(MSG.ERR_NOT_FOUND, backendMsg || MSG.ERR_NOT_FOUND_DESC);
    return;
  }
  if (errCode === ERR.INVALID_PARAM) {
    SbNotification.error(MSG.ERR_INVALID_PARAM, backendMsg);
    return;
  }
  SbNotification.error(fallbackTitle, backendMsg || fallbackDesc);
};

const useJdArchive = (email, entitlementsData, profileRole) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  const canViewRnC = userHasEntitlement(entitlementsData, profileRole, 'jobs_library', 'audit');

  const fetchData = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = canViewRnC
        ? await getRnCJobRequisitionView(email)
        : await getJobRequisitionView(email);
      setRows(Array.isArray(response) ? response : []);
    } catch {
      SbNotification.error('Failed to load job descriptions', 'Unable to fetch the requisition list. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [email, canViewRnC]);

  const archiveJd = useCallback(
    async (record) => {
      if (!record?.sb_unique_id) return;
      setActioningId(record.sb_unique_id);
      try {
        await updateJdArchiveStatus(record.sb_unique_id, JD_ARCHIVE_STATUS.ARCHIVED);
        SbNotification.success(MSG.ARCHIVE_SUCCESS);
        await fetchData();
      } catch (error) {
        handleJdArchiveError(error, MSG.ARCHIVE_FAILED, 'Unable to archive this job description. Please try again.');
      } finally {
        setActioningId(null);
      }
    },
    [fetchData],
  );

  return {
    loading,
    rows,
    fetchData,
    archiveJd,
    actioningId,
  };
};

export default useJdArchive;
